import React, { useLayoutEffect, useRef, useState } from "react";
import { ArrowRight, Lock, MoreHorizontal, X } from "lucide-react";
import BrandMark from "./BrandMark";
import {
  ENQUIRY_WELCOME_GREETING,
  ENQUIRY_WELCOME_QUESTION,
  ENQUIRY_WELCOME_QUESTION_TAIL,
  ENQUIRY_WELCOME_SUBTITLE,
  FORM_WELCOME_ACTION,
  PRE_CONSULT_WELCOME_GREETING,
  PRE_CONSULT_PREVIEW_NAME,
  PRE_CONSULT_WELCOME_HEADING,
  PRE_CONSULT_WELCOME_HEADING_TAIL,
  PRE_CONSULT_WELCOME_SUBTITLE,
} from "../../enquiries/publicFormCopy";
import type { BrandDetails, FormPreviewId } from "../types/brand.types";

/**
 * What a client sees when they open the link, drawn as a phone.
 *
 * A phone and not a plain card because that is where the link gets opened: it
 * arrives in a WhatsApp message or an Instagram bio, and the chrome above the
 * page — the close button, the address, the app the browser belongs to — is
 * part of what the practitioner is deciding about. A link that reads as
 * somebody else's domain in that strip is the thing this preview exists to
 * catch.
 *
 * Laid out at one canonical phone size and scaled to whatever width the column
 * has, the way `BrandPreviewPanel` scales the invoice: type, spacing and the
 * bezel all grow together, so a wider panel is a bigger phone rather than the
 * same phone with more air in it.
 *
 * Deliberately not the real `PublicEnquiryWelcome`. That component switches
 * layout at `sm:`, and a media query reads the window rather than the box a
 * component is scaled into — so on any desktop wide enough to show this panel
 * the real one would centre its text and stop being a picture of a phone. The
 * words come from `publicFormCopy` either way; only the layout is drawn twice.
 */

/**
 * The screen, then the bezel around it. 390 is the width the real form is
 * designed against, which is what lets the numbers below — a 46px heading, a
 * 40px gutter — be the form's own rather than a second set invented for a
 * preview.
 */
const SCREEN = { width: 390, height: 856 };
const BEZEL = 12;
const FRAME = {
  width: SCREEN.width + BEZEL * 2,
  height: SCREEN.height + BEZEL * 2,
};

/**
 * A ceiling on how wide the phone may be drawn.
 *
 * From `lg` up the column it sits in is narrower than this anyway, so it only
 * bites below that breakpoint - where the card runs the width of the page and
 * a phone scaled to fit it would be the size of a real one.
 */
const MAX_RENDERED_WIDTH = 360;

/** Tailwind's `lg` - the width at which the panel becomes a fixed column. */
const LG_BREAKPOINT = 1024;

interface ScreenCopy {
  greeting: string;
  /** The heading's second line: the enquiry form's question, and the
      pre-consultation form's "get started!". */
  question?: string;
  /** A third line, where the heading is set in three. The enquiry question is
      broken by hand on a phone and this is always a phone, so the break is
      unconditional here where the real screen hides it from `sm:` up. */
  questionTail?: string;
  /** Who the screen is addressed to, under the heading. Only the
      pre-consultation form names anybody, and the preview stands a sample name
      in for the reader it does not have - see `PRE_CONSULT_PREVIEW_NAME`. */
  greetingLine?: string;
  subtitle: string;
  /**
   * The action is held at the bottom edge of the phone and runs its full
   * width, rather than sitting inline under the last line of text.
   *
   * The pre-consultation form's, and the difference between the two welcomes:
   * its Continue lands where every Continue after it lands — see
   * `PreconsultationWelcomeAction` — while the enquiry form's sits under the
   * sentence it follows, because that form's first screen is a paragraph and a
   * button rather than a page with a floor.
   *
   * A picture of a button either way, so this is the shape and not a
   * behaviour.
   */
  actionAtFoot?: boolean;
}

const COPY: Record<FormPreviewId, ScreenCopy> = {
  enquiry: {
    greeting: ENQUIRY_WELCOME_GREETING,
    question: ENQUIRY_WELCOME_QUESTION,
    questionTail: ENQUIRY_WELCOME_QUESTION_TAIL,
    subtitle: ENQUIRY_WELCOME_SUBTITLE,
  },
  "pre-consultation": {
    greeting: PRE_CONSULT_WELCOME_HEADING,
    question: PRE_CONSULT_WELCOME_HEADING_TAIL,
    greetingLine: `${PRE_CONSULT_WELCOME_GREETING} ${PRE_CONSULT_PREVIEW_NAME}`,
    subtitle: PRE_CONSULT_WELCOME_SUBTITLE,
    actionAtFoot: true,
  },
};

/**
 * The browser's own strip above the page — an in-app browser's, which is what
 * a link tapped inside Instagram or WhatsApp opens in.
 *
 * `aria-hidden`, because none of it is real. A screen reader announcing a
 * close button that closes nothing, again every time the switch is flipped, is
 * noise in the middle of a form the practitioner is actually filling in.
 */
const BrowserChrome: React.FC<{ host: string; app: string }> = ({
  host,
  app,
}) => (
  <div
    aria-hidden="true"
    className="flex flex-shrink-0 items-center gap-3 px-5 pb-4 pt-[52px]"
  >
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F1F3F5] text-ink">
      <X size={16} strokeWidth={2.5} />
    </span>

    <div className="min-w-0 flex-1 text-center">
      <div className="flex items-center justify-center gap-1.5">
        <Lock size={11} className="flex-shrink-0 text-ink-muted" />
        <span className="truncate text-[13px] font-medium text-ink">
          {host}
        </span>
      </div>
      <div className="mt-0.5 truncate text-[11px] text-ink-subtle">{app}</div>
    </div>

    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F1F3F5] text-ink">
      <MoreHorizontal size={16} strokeWidth={2.5} />
    </span>
  </div>
);

/**
 * The button, as a picture of one.
 *
 * A `span` rather than a `button`: a real one here would put something in the
 * tab order that does nothing when it is pressed.
 */
const Action: React.FC<{ wide?: boolean }> = ({ wide }) => (
  <span
    className={`inline-flex items-center justify-center gap-2 rounded-lg bg-[#00C2CB] px-6 py-3.5 text-[15px] font-medium text-white ${
      wide ? "w-full" : "mt-10 min-w-[165px]"
    }`}
  >
    {FORM_WELCOME_ACTION}
    <ArrowRight size={18} />
  </span>
);

/**
 * The welcome screen itself, at the sizes the real one uses on a phone.
 *
 * The two forms open differently and the preview says so: the enquiry form is
 * a greeting, a sentence and a button under it; the pre-consultation form is a
 * heading, the reader's name, a sentence, and a Continue held at the foot of
 * the screen. A practitioner switching between the two here is looking at what
 * their client will actually be sent.
 */
const WelcomeScreen: React.FC<{ details: BrandDetails; copy: ScreenCopy }> = ({
  details,
  copy,
}) => (
  // `px-10` is the wide gutter the welcome screen is given on a phone.
  // `my-auto` centres the block and collapses to nothing rather than clipping
  // it, which is how the real screen behaves too — and, on the form whose
  // action is held at the foot, it is also what puts the action there: the
  // free space goes to the margins above and below the block, leaving the
  // button against the bottom edge.
  <div className="flex min-h-0 flex-1 flex-col px-10 pb-10">
    <div className="my-auto w-full">
      {/* The practitioner's own logo, at the height the form gives it, with
          the same "yourlogo" specimen standing in until one is uploaded that
          the invoice and the meal plan use. */}
      <BrandMark details={details} size={44} className="mb-20" />

      {/* A `p` and not an `h1`: the heading of this page is "Share your forms
          with clients" above it, and a 46px heading inside a picture would sit
          in the document outline as though it were one. */}
      <p className="text-[46px] font-semibold leading-[1.2] tracking-normal text-[#0A4A52]">
        {copy.greeting}
        {copy.question && (
          <>
            <br />
            {copy.question}
          </>
        )}
        {copy.questionTail && (
          <>
            <br />
            {copy.questionTail}
          </>
        )}
      </p>

      {/* The name the screen is addressed to, where the form carries one. */}
      {copy.greetingLine && (
        <p className="mt-7 text-[22px] font-medium text-brand-dark">
          {copy.greetingLine}
        </p>
      )}

      <p
        className={`mt-4 text-[18px] leading-7 ${
          copy.greetingLine ? "text-ink-muted" : "text-brand-dark"
        }`}
      >
        {copy.subtitle}
      </p>

      {/* Under the sentence, on the form that draws it there. */}
      {!copy.actionAtFoot && <Action />}
    </div>

    {/* And against the bottom edge on the form that draws it there, outside
        the block the words are centred in — which is exactly how the real
        screen is put together, where the button is the shell's footer rather
        than the last line of the card. */}
    {copy.actionAtFoot && <Action wide />}
  </div>
);

interface FormPhonePreviewProps {
  details: BrandDetails;
  /** Which of the two forms is being shown. */
  preview: FormPreviewId;
  /** The address bar's contents — the practitioner's own host once claimed. */
  host: string;
  /** The app the in-app browser belongs to, named under the address. */
  app?: string;
  /**
   * Whether the box's height limits the phone at every width, not only from
   * `lg` up. For a box that has a height of its own wherever it is drawn —
   * the sheet a phone opens this preview in, which is as tall as the sheet
   * and no taller, however wide the window is.
   */
  fitHeight?: boolean;
}

const FormPhonePreview: React.FC<FormPhonePreviewProps> = ({
  details,
  preview,
  host,
  app = "Instagram",
  fitHeight = false,
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(MAX_RENDERED_WIDTH / FRAME.width);

  // The phone is sized to whichever axis of the panel runs out first, so it is
  // always shown whole - never scrolled and never cropped. The same contract
  // the invoice preview on the Details tab keeps, and the reason this column
  // does not scroll: a preview you have to scroll to see is not a preview.
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    const measure = () => {
      // Below `lg` the card sits under the link rather than beside it and
      // takes whatever height it needs, so only its width limits the phone —
      // unless the box says it has a height to keep to.
      const heightBound = fitHeight || window.innerWidth >= LG_BREAKPOINT;

      // Fractional, unlike clientWidth/Height: rounding the box up by half a
      // pixel is enough to clip the bezel's edge.
      const { width, height } = box.getBoundingClientRect();

      const byWidth =
        Math.min(width || MAX_RENDERED_WIDTH, MAX_RENDERED_WIDTH) / FRAME.width;
      const byHeight = heightBound && height ? height / FRAME.height : Infinity;

      const next = Math.min(byWidth, byHeight);
      setScale(next > 0 && Number.isFinite(next) ? next : 1);
    };

    measure();
    window.addEventListener("resize", measure);

    // A window resize is not the only thing that moves the box: the web font
    // swapping in after first paint reflows the card around it.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    observer?.observe(box);

    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [fitHeight]);

  return (
    // `flex-1 min-h-0` rather than `h-full`, which is what makes the
    // measurement mean anything: the box takes the height the card has left
    // and cannot be grown past it by what is inside it. With `h-full` the box
    // would be as tall as the phone, the phone would be scaled to the box, and
    // a phone that started too big would stay too big for good.
    //
    // `items-center` so one shorter than the card sits in the middle of it
    // rather than hanging from the top.
    <div
      ref={boxRef}
      className="flex w-full min-h-0 flex-1 items-center justify-center overflow-hidden"
    >
      {/* The scaled footprint, so the card is sized by the phone as drawn
          rather than by the full-size layout inside it. */}
      <div
        style={{ width: FRAME.width * scale, height: FRAME.height * scale }}
        className="relative"
      >
        <div
          style={{
            width: FRAME.width,
            height: FRAME.height,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
          className="relative"
        >
          {/* The buttons down the sides — nothing but the silhouette of a
              phone, which is what makes the frame read as one at a glance. */}
          <span
            aria-hidden="true"
            className="absolute -left-[3px] top-[150px] h-[32px] w-[3px] rounded-l-sm bg-[#3A3B40]"
          />
          <span
            aria-hidden="true"
            className="absolute -left-[3px] top-[212px] h-[58px] w-[3px] rounded-l-sm bg-[#3A3B40]"
          />
          <span
            aria-hidden="true"
            className="absolute -left-[3px] top-[288px] h-[58px] w-[3px] rounded-l-sm bg-[#3A3B40]"
          />
          <span
            aria-hidden="true"
            className="absolute -right-[3px] top-[240px] h-[92px] w-[3px] rounded-r-sm bg-[#3A3B40]"
          />

          <div
            className="h-full w-full rounded-[58px] bg-[#1E1F24]"
            style={{ padding: BEZEL }}
          >
            <div className="relative flex h-full w-full flex-col overflow-hidden rounded-[46px] bg-white">
              <span
                aria-hidden="true"
                className="absolute left-1/2 top-[14px] h-[30px] w-[104px] -translate-x-1/2 rounded-full bg-[#1E1F24]"
              />

              <BrowserChrome host={host} app={app} />
              <WelcomeScreen details={details} copy={COPY[preview]} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormPhonePreview;
